import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FilterButton, FilterField } from '@/components/FilterButton'

export type StatusFilterValue = 'all' | 'ACTIVE' | 'INACTIVE'

interface StatusFilterProps {
  value: StatusFilterValue
  onChange: (value: StatusFilterValue) => void
}

export function StatusFilter({ value, onChange }: StatusFilterProps) {
  const { t } = useTranslation()

  return (
    <Select value={value} onValueChange={(v) => onChange(v as StatusFilterValue)}>
      <SelectTrigger className="h-8 w-full text-sm">
        <SelectValue>{value === 'all' ? t('common.all') : value === 'ACTIVE' ? t('common.active') : t('common.inactive')}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t('common.all')}</SelectItem>
        <SelectItem value="ACTIVE">{t('common.active')}</SelectItem>
        <SelectItem value="INACTIVE">{t('common.inactive')}</SelectItem>
      </SelectContent>
    </Select>
  )
}

interface StatusFilterButtonProps extends StatusFilterProps {
  /** The value that counts as unfiltered, and what reset goes back to. */
  defaultValue?: StatusFilterValue
  /** Extra `FilterField` entries shown below the status field. */
  children?: ReactNode
  /** Active filters other than the status one, added to the button badge. */
  extraActiveCount?: number
  /** Resets every field — needed when `children` add filters of their own. */
  onReset?: () => void
}

/**
 * The status filter as a list toolbar button — the shape nearly every list
 * uses. Pass `children` to add list specific fields to the same popover.
 */
export function StatusFilterButton({
  value,
  onChange,
  defaultValue = 'ACTIVE',
  children,
  extraActiveCount = 0,
  onReset,
}: StatusFilterButtonProps) {
  const { t } = useTranslation()

  return (
    <FilterButton
      activeCount={(value !== defaultValue ? 1 : 0) + extraActiveCount}
      onReset={onReset ?? (() => onChange(defaultValue))}
    >
      <FilterField label={t('common.status')}>
        <StatusFilter value={value} onChange={onChange} />
      </FilterField>
      {children}
    </FilterButton>
  )
}
